import React from "react";
import { IconButton, InputAdornment } from "@mui/material";

import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import PartsListWithCheckbox from "@/main/components/shared/partsListWithCheckbox/partsListWithCheckbox";

import { GetItemsByStatusResponse } from "@/main/models/response";
import { getTranslateByKey } from "@/main/services/utils";
import * as S from "./styles";
import ChangeStatusFileUpload from "../changeStatus/changeStatusFileUpload";
import { ChangeStatusItemsRequest, ProcurementSupplierItemsRequest } from "@/main/models/request";
import { useForm } from "react-hook-form";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import KogInputText from "@/main/components/shared/inputText/inputText";
import { editSquareSVG } from "@/main/components/shared/icons/svg/editSquareSVG";
import { Controls } from "@/main/services/constants/constantsControl";
import TooltipComponent from "@/main/components/shared/tooltip/tooltipComponent";
import useKogControlElementId from "@/main/hooks/useKogControlElementId";

interface ITransitForm {
	shippingName: string;
	trackingCode: string;
}

interface IExtraGroupForm {
	key: string;
	partGuids: string[];
	shippingName: string;
	trackingCode: string;
	initialShippingName: string;
	initialTrackingCode: string;
	isShippingNameEditable: boolean;
	isTrackingCodeEditable: boolean;
	shippingNameError: boolean;
	trackingCodeError: boolean;
}

interface IGroupedOptions {
	key: string;
	shippingName: string;
	trackingCode: string;
	parts: GetItemsByStatusResponse[];
}

interface IAssignedToInTransit {
	confirmMessage: string;
	enableEditShippingControl?: boolean;
	closeModal: () => void;
	options: GetItemsByStatusResponse[];
	optionsSelected: string[];
	setOptionsSelected: React.Dispatch<React.SetStateAction<string[]>>;
	files: File[];
	setFiles: React.Dispatch<React.SetStateAction<File[]>>;
	handleSubmitChangeStatus(
		extraData?: Pick<ChangeStatusItemsRequest, "shippingName" | "trackingCode">,
		procurementSupplierItems?: ProcurementSupplierItemsRequest[],
	): void;
	loadingSubmit: boolean;
	loadingOptions: boolean;
}

function createGroupKey(shippingName?: string, trackingCode?: string) {
	return `${shippingName || ""}__${trackingCode || ""}`;
}

function AssignedToInTransit({
	confirmMessage,
	enableEditShippingControl = false,
	closeModal,
	options,
	optionsSelected,
	setOptionsSelected,
	files,
	setFiles,
	handleSubmitChangeStatus,
	loadingSubmit,
	loadingOptions,
}: IAssignedToInTransit) {
	const canEditShippingInformation = useKogControlElementId(Controls.IdBtnEditShippingInformation);
	const INITIAL_FORM_STATE = {
		shippingName: "",
		trackingCode: "",
	};
	const [initialShippingName, setInitialShippingName] = React.useState("");
	const [initialTrackingCode, setInitialTrackingCode] = React.useState("");
	const [isShippingNameEditable, setIsShippingNameEditable] = React.useState(false);
	const [isTrackingCodeEditable, setIsTrackingCodeEditable] = React.useState(false);
	const [extraGroupsForm, setExtraGroupsForm] = React.useState<IExtraGroupForm[]>([]);

	const groupedOptions = React.useMemo<IGroupedOptions[]>(() => {
		const groupedMap = new Map<string, IGroupedOptions>();

		options.forEach((option) => {
			const shippingName = option.shippingName || "";
			const trackingCode = option.trackingCode || "";
			const key = createGroupKey(shippingName, trackingCode);

			if (!groupedMap.has(key)) {
				groupedMap.set(key, {
					key,
					shippingName,
					trackingCode,
					parts: [],
				});
			}

			const currentGroup = groupedMap.get(key);
			if (currentGroup) {
				currentGroup.parts.push(option);
			}
		});

		return Array.from(groupedMap.values());
	}, [options]);

	const primaryGroup = groupedOptions[0];
	const extraGroups = React.useMemo(() => groupedOptions.slice(1), [groupedOptions]);

	const { control, handleSubmit, setError, setValue, clearErrors } = useForm<ITransitForm>({
		mode: "all",
		defaultValues: INITIAL_FORM_STATE,
	});

	React.useEffect(() => {
		const shippingNameFromApi = primaryGroup?.shippingName || "";
		const trackingCodeFromApi = primaryGroup?.trackingCode || "";

		setInitialShippingName(shippingNameFromApi);
		setInitialTrackingCode(trackingCodeFromApi);
		setValue("shippingName", shippingNameFromApi);
		setValue("trackingCode", trackingCodeFromApi);
		clearErrors(["shippingName", "trackingCode"]);
		setIsShippingNameEditable(false);
		setIsTrackingCodeEditable(false);
	}, [primaryGroup, setValue, clearErrors]);

	React.useEffect(() => {
		setExtraGroupsForm(
			extraGroups.map((group) => ({
				key: group.key,
				partGuids: group.parts.map((x) => x.guidProcurementSupplierItem),
				shippingName: group.shippingName,
				trackingCode: group.trackingCode,
				initialShippingName: group.shippingName,
				initialTrackingCode: group.trackingCode,
				isShippingNameEditable: false,
				isTrackingCodeEditable: false,
				shippingNameError: false,
				trackingCodeError: false,
			})),
		);
	}, [extraGroups]);

	const isShippingNameDisabled = enableEditShippingControl && !isShippingNameEditable;
	const isTrackingCodeDisabled = enableEditShippingControl && !isTrackingCodeEditable;

	function toggleShippingNameEditable() {
		setIsShippingNameEditable((prevState) => {
			const nextState = !prevState;
			if (!nextState) {
				setValue("shippingName", initialShippingName);
				clearErrors("shippingName");
			}
			return nextState;
		});
	}

	function toggleTrackingCodeEditable() {
		setIsTrackingCodeEditable((prevState) => {
			const nextState = !prevState;
			if (!nextState) {
				setValue("trackingCode", initialTrackingCode);
				clearErrors("trackingCode");
			}
			return nextState;
		});
	}

	function handleChangeExtraShippingName(index: number, value: string) {
		setExtraGroupsForm((prev) =>
			prev.map((group, groupIndex) =>
				groupIndex === index
					? {
							...group,
							shippingName: value,
							shippingNameError: false,
						}
					: group,
			),
		);
	}

	function handleChangeExtraTrackingCode(index: number, value: string) {
		setExtraGroupsForm((prev) =>
			prev.map((group, groupIndex) =>
				groupIndex === index
					? {
							...group,
							trackingCode: value,
							trackingCodeError: false,
						}
					: group,
			),
		);
	}

	function toggleExtraShippingNameEditable(index: number) {
		setExtraGroupsForm((prev) =>
			prev.map((group, groupIndex) => {
				if (groupIndex !== index) return group;

				const nextState = !group.isShippingNameEditable;

				return {
					...group,
					isShippingNameEditable: nextState,
					shippingName: nextState ? group.shippingName : group.initialShippingName,
					shippingNameError: false,
				};
			}),
		);
	}

	function toggleExtraTrackingCodeEditable(index: number) {
		setExtraGroupsForm((prev) =>
			prev.map((group, groupIndex) => {
				if (groupIndex !== index) return group;

				const nextState = !group.isTrackingCodeEditable;

				return {
					...group,
					isTrackingCodeEditable: nextState,
					trackingCode: nextState ? group.trackingCode : group.initialTrackingCode,
					trackingCodeError: false,
				};
			}),
		);
	}

	const showEditIcon = enableEditShippingControl && canEditShippingInformation;

	function onSubmit(data: ITransitForm) {
		const selectedGuids = new Set(optionsSelected);
		const procurementSupplierItems: ProcurementSupplierItemsRequest[] = [];
		const primaryGroupPartGuids = new Set(primaryGroup?.parts.map((x) => x.guidProcurementSupplierItem) || []);
		const hasSelectedPrimaryPart = optionsSelected.some((guid) => primaryGroupPartGuids.has(guid));

		if (isShippingMandatory) {
			let hasError = false;

			if (hasSelectedPrimaryPart) {
				if (data.shippingName.trim() === "") {
					setError("shippingName", { type: "required" });
					hasError = true;
				}

				if (data.trackingCode.trim() === "") {
					setError("trackingCode", { type: "required" });
					hasError = true;
				}
			}

			const updatedExtraGroups = extraGroupsForm.map((group) => {
				const hasSelectedPart = group.partGuids.some((guid) => selectedGuids.has(guid));

				if (!hasSelectedPart) {
					return {
						...group,
						shippingNameError: false,
						trackingCodeError: false,
					};
				}

				const shippingNameError = group.shippingName.trim() === "";
				const trackingCodeError = group.trackingCode.trim() === "";

				if (shippingNameError || trackingCodeError) {
					hasError = true;
				}

				return {
					...group,
					shippingNameError,
					trackingCodeError,
				};
			});

			setExtraGroupsForm(updatedExtraGroups);

			if (hasError) return;
		}

		optionsSelected.forEach((selectedGuid) => {
			const extraGroup = extraGroupsForm.find((group) => group.partGuids.includes(selectedGuid));

			if (extraGroup) {
				procurementSupplierItems.push({
					guidProcurementSupplierItem: selectedGuid,
					shippingName: extraGroup.shippingName,
					trackingCode: extraGroup.trackingCode,
				});
				return;
			}

			if (hasSelectedPrimaryPart) {
				procurementSupplierItems.push({
					guidProcurementSupplierItem: selectedGuid,
					shippingName: data.shippingName,
					trackingCode: data.trackingCode,
				});
			}
		});

		const firstItem = procurementSupplierItems[0];

		handleSubmitChangeStatus(
			firstItem
				? {
						shippingName: firstItem.shippingName,
						trackingCode: firstItem.trackingCode,
					}
				: undefined,
			procurementSupplierItems,
		);
	}

	const isShippingMandatory = options.some((x) => x.isMandatoryShippingInformation);
	const MAX_LENGTH = 20;

	return (
		<>
			<S.Content>
				<PartsListWithCheckbox
					parts={primaryGroup?.parts || []}
					optionsSelected={optionsSelected}
					setOptionsSelected={setOptionsSelected}
					isLoading={loadingOptions}
				/>
			</S.Content>

			<form onSubmit={handleSubmit(onSubmit)} noValidate>
				<S.Content>
					<KogInputTextControlledNew
						control={control}
						name="shippingName"
						disabled={isShippingNameDisabled}
						required={isShippingMandatory}
						label={getTranslateByKey("information.deliveryService")}
						inputProps={{ maxLength: MAX_LENGTH }}
						InputProps={{
							endAdornment: showEditIcon ? (
								<InputAdornment position="end">
									<TooltipComponent title={getTranslateByKey("information.edit")}>
										<IconButton
											type="button"
											id="id-assignedToInTransit-button-edit-shippingName"
											onClick={toggleShippingNameEditable}
											style={{ cursor: "pointer" }}
										>
											{editSquareSVG()}
										</IconButton>
									</TooltipComponent>
								</InputAdornment>
							) : undefined,
						}}
						id="id-assignedToInTransit-input-shippingName"
					/>

					<KogInputTextControlledNew
						control={control}
						name="trackingCode"
						disabled={isTrackingCodeDisabled}
						required={isShippingMandatory}
						label={getTranslateByKey("information.trackingCode")}
						inputProps={{ maxLength: MAX_LENGTH }}
						InputProps={{
							endAdornment: showEditIcon ? (
								<InputAdornment position="end">
									<TooltipComponent title={getTranslateByKey("information.edit")}>
										<IconButton
											type="button"
											id="id-assignedToInTransit-button-edit-trackingCode"
											onClick={toggleTrackingCodeEditable}
											style={{ cursor: "pointer" }}
										>
											{editSquareSVG()}
										</IconButton>
									</TooltipComponent>
								</InputAdornment>
							) : undefined,
						}}
						id="id-assignedToInTransit-input-trackingCode"
					/>
				</S.Content>

				{extraGroups.map((group, index) => {
					const extraGroupForm = extraGroupsForm[index];
					if (!extraGroupForm) return null;

					const isExtraShippingNameDisabled = enableEditShippingControl && !extraGroupForm.isShippingNameEditable;
					const isExtraTrackingCodeDisabled = enableEditShippingControl && !extraGroupForm.isTrackingCodeEditable;

					return (
						<React.Fragment key={group.key}>
							<S.Content>
								<PartsListWithCheckbox
									parts={group.parts}
									optionsSelected={optionsSelected}
									setOptionsSelected={setOptionsSelected}
								/>
							</S.Content>

							<S.Content>
								<KogInputText
									value={extraGroupForm.shippingName}
									disabled={isExtraShippingNameDisabled}
									required={isShippingMandatory}
									label={getTranslateByKey("information.deliveryService")}
									inputProps={{ maxLength: MAX_LENGTH }}
									errorMessageCustom={
										extraGroupForm.shippingNameError ? getTranslateByKey("information.required") : null
									}
									InputProps={{
										endAdornment: showEditIcon ? (
											<InputAdornment position="end">
												<TooltipComponent title={getTranslateByKey("information.edit")}>
													<IconButton
														type="button"
														id={`id-assignedToInTransit-button-edit-shippingName-extra-${index}`}
														onClick={() => toggleExtraShippingNameEditable(index)}
														style={{ cursor: "pointer" }}
													>
														{editSquareSVG()}
													</IconButton>
												</TooltipComponent>
											</InputAdornment>
										) : undefined,
									}}
									id={`id-assignedToInTransit-input-shippingName-extra-${index}`}
									onChangeCustom={(value) => handleChangeExtraShippingName(index, value)}
								/>

								<KogInputText
									value={extraGroupForm.trackingCode}
									disabled={isExtraTrackingCodeDisabled}
									required={isShippingMandatory}
									label={getTranslateByKey("information.trackingCode")}
									inputProps={{ maxLength: MAX_LENGTH }}
									errorMessageCustom={
										extraGroupForm.trackingCodeError ? getTranslateByKey("information.required") : null
									}
									InputProps={{
										endAdornment: showEditIcon ? (
											<InputAdornment position="end">
												<TooltipComponent title={getTranslateByKey("information.edit")}>
													<IconButton
														type="button"
														id={`id-assignedToInTransit-button-edit-trackingCode-extra-${index}`}
														onClick={() => toggleExtraTrackingCodeEditable(index)}
														style={{ cursor: "pointer" }}
													>
														{editSquareSVG()}
													</IconButton>
												</TooltipComponent>
											</InputAdornment>
										) : undefined,
									}}
									id={`id-assignedToInTransit-input-trackingCode-extra-${index}`}
									onChangeCustom={(value) => handleChangeExtraTrackingCode(index, value)}
								/>
							</S.Content>
						</React.Fragment>
					);
				})}

				<ChangeStatusFileUpload files={files} setFiles={setFiles} />
				<S.ContainerButton>
					<SecondaryButton onClick={closeModal} id="id-assignedToInTransit-button-back">
						{getTranslateByKey("information.back")}
					</SecondaryButton>
					<SecondaryButton
						disabled={optionsSelected.length == 0 || loadingSubmit}
						type="submit"
						isLoading={loadingSubmit}
						id="id-assignedToInTransit-button-submit"
					>
						{confirmMessage}
					</SecondaryButton>
				</S.ContainerButton>
			</form>
		</>
	);
}

export default AssignedToInTransit;
